import { inject, Injectable } from '@angular/core';
import { NavData, NavSection, SubNavLink } from '@terminus-ui/nav';
import { EntitlementsService } from 'entitlements';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class NavItemAccessService {
  private entitlementService = inject(EntitlementsService);

  filterNavItemsByRestrictions$<T extends NavData | NavSection | SubNavLink[]>(
    navItems: T,
  ): Observable<T> {
    const filteredItems$ = navItems.map((navItem) => {
      if (Array.isArray(navItem)) {
        // If we have a list of items, recurse
        return this.filterNavItemsByRestrictions$(navItem);
      } else {
        // If we are at a leaf node (i.e. a NavItem), or a MenuItem. determine if it should be shown
        return this.entitlementService.getEntitlement$(navItem).pipe(
          switchMap((shouldShow) => {
            if (
              shouldShow === true &&
              'items' in navItem &&
              Array.isArray(navItem.items) &&
              navItem.items.length > 0
            ) {
              return this.filterNavItemsByRestrictions$(navItem.items).pipe(
                map((items) =>
                  items.length === 0 ? null : { ...navItem, items },
                ),
              );
            } else {
              return of(shouldShow === true ? navItem : null);
            }
          }),
        );
      }
    });
    return combineLatest(filteredItems$).pipe(
      map((items) => items.filter(isNotNullOrEmpty) as T),
    );
  }
}

export function isNotNullOrEmpty<T>(arg: unknown): arg is T {
  return arg !== null && (!Array.isArray(arg) || arg.length > 0);
}
